import { useMemo } from "react";

const imageWidths = [
  16, 32, 48, 64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920, 2048,
  3840,
] as const;

type ImgWidth = (typeof imageWidths)[number];

// `/cdn-cgi/image/` only exists once Image Transformations are enabled for the
// Cloudflare zone, and never on `*.workers.dev` or in local dev. Until then the
// original files are served.
const imageTransformsEnabled =
  import.meta.env.PROD &&
  import.meta.env.VITE_CLOUDFLARE_IMAGE_TRANSFORMS === "true";

const getOptimizedUrl = (url: string, width: ImgWidth) =>
  `/cdn-cgi/image/width=${width},quality=60,format=auto${url}`;

export const useOptimizedImageProps = (
  src: string,
  width: number,
  height: number
) =>
  useMemo(() => {
    if (!imageTransformsEnabled) return { src, width, height };
    const widths = [
      ...new Set(
        [width, width * 2].map(
          (w) => imageWidths.find((p) => p >= w) || imageWidths.at(-1) || 3840
        )
      ),
    ] as [ImgWidth, ...ImgWidth[]];
    return {
      srcSet: widths
        .map((w, i) => `${getOptimizedUrl(src, w)} ${i + 1}x`)
        .join(", "),
      width: widths[0],
      height: Math.round((widths[0] * height) / width),
    };
  }, [src, width, height]);

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  width: number;
  height: number;
  alt: string;
}

export const useOptimizedImageArrayProps = (
  srcs: string[],
  width: number,
  height: number
) =>
  useMemo(() => {
    if (!imageTransformsEnabled)
      return srcs.map((src) => ({ src, width, height }));

    const widths = [
      ...new Set(
        [width, width * 2].map(
          (w) => imageWidths.find((p) => p >= w) || imageWidths.at(-1) || 3840
        )
      ),
    ] as [ImgWidth, ...ImgWidth[]];

    return srcs.map((src) => ({
      src: getOptimizedUrl(src, widths.at(-1) || 3840),
      srcSet: widths
        .map((w, i) => `${getOptimizedUrl(src, w)} ${i + 1}x`)
        .join(", "),
      width: widths[0],
      height: Math.round((widths[0] * height) / width),
    }));
  }, [srcs, width, height]);

export const Image = ({
  src,
  width,
  height,
  loading = "lazy",
  ...props
}: ImageProps) => {
  const imgProps = useOptimizedImageProps(src, width, height);
  return <img loading={loading} {...imgProps} {...props} />;
};
